import React, { useState } from 'react';
import useAddRecipe from '../../hooks/addRecipe';

const styles = {
  formContainer: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem',
  },
  formTitle: {
    color: '#333',
    marginBottom: '2rem',
    fontSize: '2rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
  },
  formSection: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  sectionTitle: {
    color: '#52cd92',
    marginBottom: '1.5rem',
    fontSize: '1.5rem',
  },
  formGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
    transition: 'border-color 0.2s',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
    minHeight: '100px',
    resize: 'vertical',
  },
  checkboxGroup: {
    display: 'flex',
    gap: '2rem',
    marginBottom: '1.5rem',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
  },
  nutritionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
  },
  submitButton: {
    backgroundColor: '#52cd92',
    color: 'white',
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'all 0.2s',
    alignSelf: 'flex-start',
    marginTop: '1rem',
  },
};

function RecipeForm() {
    const [recipe, setRecipe] = useState({
        name: '',
        description: '',
        prep_time: '',
        cook_time: '',
        servings: '',
        difficulty: '',
        instructions: '',
        image_url: '',
        is_vegetarian: false,
        is_vegan: false,
        protein: '',
        carbohydrate: '',
        fat: '',
        calories: '',
        micronutrients: '',
        cost_per_serving: '',
        taste_profile: '',
        tags: '',
        equipment: '',
        author_id: '',
    });

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setRecipe({
            ...recipe,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const { addRecipe, isLoading } = useAddRecipe();

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { protein, carbohydrate, fat, calories, ...rest } = recipe;
        const payload = {
            ...rest,
            nutritionalInfo: { protein, carbohydrate, fat, calories }
        };
        const result = await addRecipe(payload);
        
        if (result.success) {
            alert(result.message);
            setRecipe({
                name: '',
                description: '',
                prep_time: '',
                cook_time: '',
                servings: '',
                difficulty: '',
                instructions: '',
                image_url: '',
                is_vegetarian: false,
                is_vegan: false,
                protein: '',
                carbohydrate: '',
                fat: '',
                calories: '',
                micronutrients: '',
                cost_per_serving: '',
                taste_profile: '',
                tags: '',
                equipment: ''
            });
        } else {
            alert(result.message);
        }
    };

    return (
        <div style={styles.formContainer}>
            <h1 style={styles.formTitle}>Create New Recipe</h1>
            <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.formSection}>
                    <h2 style={styles.sectionTitle}>Basic Information</h2>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            Recipe Name
                            <input
                                type="text"
                                name="name"
                                value={recipe.name}
                                onChange={handleChange}
                                required
                                style={styles.input}
                            />
                        </label>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            Description
                            <textarea
                                name="description"
                                value={recipe.description}
                                onChange={handleChange}
                                required
                                style={styles.textarea}
                            />
                        </label>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            Image URL
                            <input
                                type="text"
                                name="image_url"
                                value={recipe.image_url}
                                onChange={handleChange}
                                style={styles.input}
                            />
                        </label>
                    </div>

                    <div style={styles.checkboxGroup}>
                        <label style={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                name="is_vegetarian"
                                checked={recipe.is_vegetarian}
                                onChange={handleChange}
                            />
                            Vegetarian
                        </label>

                        <label style={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                name="is_vegan"
                                checked={recipe.is_vegan}
                                onChange={handleChange}
                            />
                            Vegan
                        </label>
                    </div>
                </div>

                <div style={styles.formSection}>
                    <h2 style={styles.sectionTitle}>Cooking Details</h2>
                    <div style={styles.nutritionGrid}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Prep Time (minutes)
                                <input
                                    type="number"
                                    name="prep_time"
                                    value={recipe.prep_time}
                                    onChange={handleChange}
                                    style={styles.input}
                                />
                            </label>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Cook Time (minutes)
                                <input
                                    type="number"
                                    name="cook_time"
                                    value={recipe.cook_time}
                                    onChange={handleChange}
                                    style={styles.input}
                                />
                            </label>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Servings
                                <input
                                    type="number"
                                    name="servings"
                                    value={recipe.servings}
                                    onChange={handleChange}
                                    style={styles.input}
                                />
                            </label>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Difficulty
                                <input
                                    type="text"
                                    name="difficulty"
                                    value={recipe.difficulty}
                                    onChange={handleChange}
                                    style={styles.input}
                                />
                            </label>
                        </div>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            Instructions
                            <textarea
                                name="instructions"
                                value={recipe.instructions}
                                onChange={handleChange}
                                style={styles.textarea}
                            />
                        </label>
                    </div>
                </div>

                <div style={styles.formSection}>
                    <h2 style={styles.sectionTitle}>Nutrition Information</h2>
                    <div style={styles.nutritionGrid}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Calories
                                <input
                                    type="number"
                                    name="calories"
                                    value={recipe.calories}
                                    onChange={handleChange}
                                    style={styles.input}
                                />
                            </label>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Protein (g)
                                <input
                                    type="number"
                                    name="protein"
                                    value={recipe.protein}
                                    onChange={handleChange}
                                    style={styles.input}
                                />
                            </label>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Carbohydrate (g)
                                <input
                                    type="number"
                                    name="carbohydrate"
                                    value={recipe.carbohydrate}
                                    onChange={handleChange}
                                    style={styles.input}
                                />
                            </label>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Fat (g)
                                <input
                                    type="number"
                                    name="fat"
                                    value={recipe.fat}
                                    onChange={handleChange}
                                    style={styles.input}
                                />
                            </label>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Cost per Serving
                                <input
                                    type="number"
                                    name="cost_per_serving"
                                    value={recipe.cost_per_serving}
                                    onChange={handleChange}
                                    style={styles.input}
                                />
                            </label>
                        </div>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            Micronutrients (JSON)
                            <textarea
                                name="micronutrients"
                                value={recipe.micronutrients}
                                onChange={handleChange}
                                style={styles.textarea}
                            />
                        </label>
                    </div>
                </div>

                <div style={styles.formSection}>
                    <h2 style={styles.sectionTitle}>Additional Information</h2>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            Taste Profile (JSON)
                            <textarea
                                name="taste_profile"
                                value={recipe.taste_profile}
                                onChange={handleChange}
                                style={styles.textarea}
                            />
                        </label>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            Tags (comma-separated)
                            <input
                                type="text"
                                name="tags"
                                value={recipe.tags}
                                onChange={handleChange}
                                style={styles.input}
                            />
                        </label>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            Equipment (comma-separated)
                            <input
                                type="text"
                                name="equipment"
                                value={recipe.equipment}
                                onChange={handleChange}
                                style={styles.input}
                            />
                        </label>
                    </div>


                </div>

                <button 
                    type="submit" 
                    style={{
                        ...styles.submitButton,
                        opacity: isLoading ? 0.7 : 1,
                        cursor: isLoading ? 'not-allowed' : 'pointer'
                    }}
                    disabled={isLoading}
                >
                    {isLoading ? 'Creating Recipe...' : 'Create Recipe'}
                </button>
            </form>
        </div>
    );
}

export default RecipeForm;